import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Clock, Gift, Sparkles, ArrowRight, ChevronLeft, ChevronRight, Star, Heart, Trophy, Zap, Shield, Users, Target, Award, Dumbbell } from "lucide-react";
import axios from "axios";
import { uploadSrcSet, srcSetFallback } from "@/lib/imgVariants";
import { trackCta } from "@/lib/track";

const API = `/api`;

const iconMap = {
  Gift, Sparkles, Clock, Star, Heart, Trophy, Zap, Shield, Users, Target, Award, Dumbbell
};

// Imagens do carrossel - versão desktop (horizontal) e mobile (vertical)
const defaultBanners = [];

const preloadedBanners =
  typeof window !== "undefined" && Array.isArray(window.__JF_BANNERS__) && window.__JF_BANNERS__.length > 0
    ? window.__JF_BANNERS__
    : null;

export const Campaign = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 768);
  const [banners, setBanners] = useState(preloadedBanners || []);
  const [loaded, setLoaded] = useState(!!preloadedBanners);

  const [campaignSettings, setCampaignSettings] = useState(null);
  const [centralCampaign, setCentralCampaign] = useState(null);
  const [campaignItems, setCampaignItems] = useState([]);

  // Load banners and campaign data from API
  useEffect(() => {
    const loadData = async () => {
      try {
        const [bannersRes, campaignRes] = await Promise.all([
          preloadedBanners ? Promise.resolve(null) : axios.get(`${API}/content/banners`).catch(() => ({ data: [] })),
          axios.get(`${API}/content/campaign`).catch(() => ({ data: null })),
        ]);
        if (bannersRes && bannersRes.data && bannersRes.data.length > 0) {
          setBanners(bannersRes.data);
        }
        if (campaignRes.data) {
          if (campaignRes.data.settings) setCampaignSettings(campaignRes.data.settings);
          if (campaignRes.data.items && campaignRes.data.items.length > 0) setCampaignItems(campaignRes.data.items);
        }
        const centralRes = await axios.get(`${API}/content/active-campaign?club=homepage`).catch(() => ({ data: null }));
        if (centralRes.data?.campaign) {
          setCentralCampaign(centralRes.data.campaign);
          window.__JF_ACTIVE_CAMPAIGN = centralRes.data.campaign;
        }
      } catch (error) {
        // No data available
      }
      setLoaded(true);
    };
    loadData();
  }, []);

  // Detectar se é mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Auto-rotate carousel - reset timer on manual change
  const [slideKey, setSlideKey] = useState(0);
  useEffect(() => {
    if (banners.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % banners.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [banners.length, slideKey]);

  const scrollToContact = (source) => {
    trackCta(source || "home_campanha_cta");
    const element = document.querySelector("#contacto");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setSlideKey((k) => k + 1);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % banners.length);
    setSlideKey((k) => k + 1);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + banners.length) % banners.length);
    setSlideKey((k) => k + 1);
  };

  const currentImage = banners[currentSlide];

  // Hide entire section if no banners and no campaign items
  if (loaded && banners.length === 0 && campaignItems.length === 0 && !centralCampaign) {
    return null;
  }

  return (
    <section id="campanha" className="bg-[#FAF8F5] pt-20" data-testid="campaign-section">
      {centralCampaign && (
        <div className="bg-[#1A1A1A] py-10" data-testid="central-campaign-block">
          <div className="max-w-7xl mx-auto px-4 lg:px-8">
            {centralCampaign.image_url || centralCampaign.image_url_mobile ? (
              <div className="w-full mb-8">
                <img src={centralCampaign.image_url_mobile || centralCampaign.image_url} alt={centralCampaign.title} className="w-full rounded-xl object-cover md:hidden" loading="lazy" />
                <img src={centralCampaign.image_url || centralCampaign.image_url_mobile} alt={centralCampaign.title} className="w-full rounded-xl object-cover hidden md:block" loading="lazy" />
              </div>
            ) : null}
            <div className="text-center">
              <h2 className="text-3xl md:text-4xl font-black text-[#FED601] mb-2">{centralCampaign.title}</h2>
              {centralCampaign.subtitle ? <p className="text-white/80 text-lg mb-4">{centralCampaign.subtitle}</p> : null}
              {centralCampaign.benefits?.length > 0 && (
                <div className="flex flex-wrap gap-2 justify-center mb-5">
                  {centralCampaign.benefits.map((b, i) => (
                    <span key={i} className="bg-white/10 text-white text-sm font-semibold px-3 py-1.5 rounded-full">{b}</span>
                  ))}
                </div>
              )}
              <button onClick={() => scrollToContact("home_campanha_central_cta")} className="bg-[#FED601] text-black px-8 py-3.5 font-bold uppercase tracking-wider rounded-lg hover:bg-[#E0BC00] transition-colors" data-testid="central-campaign-cta">
                {centralCampaign.cta_text || "Quero saber mais"}
              </button>
              {centralCampaign.conditions_text ? <p className="text-white/40 text-xs mt-4">{centralCampaign.conditions_text}</p> : null}
            </div>
          </div>
        </div>
      )}
      {/* Campaign Carousel - reserva o espaço enquanto carrega para evitar layout shift (CLS) */}
      {(banners.length > 0 || !loaded) && (
      <div className="relative">

        {/* Carousel Container */}
        <div className="relative overflow-hidden bg-[#FAF8F5]" style={{ minHeight: isMobile ? '75vh' : '500px' }}>
          {/* Sizer: define a altura do carrossel pela proporção real da imagem (desktop) */}
          {!isMobile && banners.length > 0 && (
            <img
              src={banners[0].desktop_image || banners[0].desktop}
              srcSet={uploadSrcSet(banners[0].desktop_image || banners[0].desktop)}
              sizes="100vw"
              alt=""
              aria-hidden="true"
              className="w-full h-auto max-h-[85vh] invisible pointer-events-none"
            />
          )}
          {/* All images stacked, only current one visible */}
          {banners.map((banner, index) => (
            <motion.div
              key={banner.id || index}
              initial={false}
              animate={{ 
                opacity: index === currentSlide ? 1 : 0,
                zIndex: index === currentSlide ? 1 : 0
              }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
              className="absolute inset-0 flex items-center justify-center"
            >
              {/* Image */}
              <img
                src={isMobile ? (banner.mobile_image || banner.mobile) : (banner.desktop_image || banner.desktop)}
                srcSet={uploadSrcSet(isMobile ? (banner.mobile_image || banner.mobile) : (banner.desktop_image || banner.desktop))}
                sizes="100vw"
                onError={srcSetFallback}
                alt={banner.alt}
                className={isMobile ? "h-full w-auto max-w-none object-cover" : "w-full h-full object-contain"}
                loading={index === 0 ? "eager" : "lazy"}
                fetchPriority={index === 0 ? "high" : "auto"}
                decoding={index === 0 ? "sync" : "async"}
              />

              {/* Overlay for banners with overlay content */}
              {(banner.overlay_title || banner.overlay) && index === currentSlide && (
                <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent flex items-center">
                  <div className="max-w-7xl mx-auto px-6 lg:px-12 w-full">
                    <motion.div
                      initial={{ opacity: 0, x: -30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 }}
                      className="max-w-lg"
                    >
                      <span className="text-[#FED601] text-sm font-bold uppercase tracking-widest">
                        Justfit
                      </span>
                      <h2 className="text-4xl lg:text-6xl font-black text-white mt-2 mb-4">
                        {banner.overlay_title || banner.overlay?.title}
                      </h2>
                      <p className="text-white/90 text-xl mb-2">
                        {banner.overlay_subtitle || banner.overlay?.subtitle}
                      </p>
                      <p className="text-white/70 mb-6">
                        {banner.overlay_description || banner.overlay?.description}
                      </p>
                      <button
                        onClick={() => scrollToContact("home_banner_saber_mais")}
                        className="bg-[#FED601] text-black px-6 py-3 font-bold uppercase tracking-wider hover:bg-[#E0BC00] transition-colors"
                      >
                        Saber Mais
                      </button>
                    </motion.div>
                  </div>
                </div>
              )}
            </motion.div>
          ))}

          {/* Navigation Arrows + Dots */}
          {banners.length > 1 && (
          <>
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors"
            aria-label="Banner anterior"
            data-testid="carousel-prev"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors"
            aria-label="Banner seguinte"
            data-testid="carousel-next"
          >
            <ChevronRight size={24} />
          </button>

          {/* Dots */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
            {banners.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                aria-label={`Ir para banner ${index + 1}`}
                className={`w-3 h-3 rounded-full transition-all ${
                  index === currentSlide ? "bg-[#FED601] w-8" : "bg-white/50 hover:bg-white/70"
                }`}
              />
            ))}
          </div>
          </>
          )}
        </div>
      </div>
      )}

      {/* Content Below Image */}
    </section>
  );
};
